import { useCallback } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Drafts written before this bucket was versioned carry no `version` field,
 * and zustand only runs `migrate` when one is present — so those can never be
 * migrated, only abandoned. The bucket was therefore renamed once; the old one
 * is cleared below so it does not linger in localStorage.
 */
const STORAGE_KEY        = 'rentflow-wizard-drafts-v2'
const STORAGE_KEY_LEGACY = 'rentflow-wizard-drafts'

if (typeof localStorage !== 'undefined') {
	localStorage.removeItem(STORAGE_KEY_LEGACY)
}

interface WizardDraft {
	state:   unknown
	stepIdx: number
	/**
	 * Версия записи, из которой черновик вырос — обычно её `updatedAt`.
	 *
	 * Черновик хранится до успешного сохранения, а запись за это время могли
	 * изменить в другом окне. Без этой метки старый черновик молча ложится
	 * поверх свежих данных: поле показывает пустоту там, где значение есть, а
	 * сохранение записывает пустоту обратно.
	 */
	version?: string
}

interface WizardDraftsState {
	drafts: Record<string, WizardDraft>
	getDraft:   (key: string) => WizardDraft | undefined
	setDraft:   (key: string, draft: WizardDraft) => void
	clearDraft: (key: string) => void
}

export const useWizardDraftsStore = create<WizardDraftsState>()(
	persist(
		(set, get) => ({
			drafts: {},
			getDraft: (key) => get().drafts[key],
			setDraft: (key, draft) =>
				set((s) => ({ drafts: { ...s.drafts, [key]: draft } })),
			clearDraft: (key) =>
				set((s) => {
					const next = { ...s.drafts }
					delete next[key]
					return { drafts: next }
				}),
		}),
		{
			name: STORAGE_KEY,
			/**
			 * Drafts hold raw form values, so they outlive the schema that
			 * produced them: a draft saved before a default changed restores the
			 * old value and hides the change from anyone who opened the wizard
			 * once. Bump this whenever a wizard's schema or defaults change —
			 * stale drafts are dropped rather than migrated, since a partially
			 * filled form is cheap to redo and a wrong one is not. Entries now
			 * carry a version, so future bumps do reach existing drafts.
			 */
			version: 1,
			migrate: () => ({ drafts: {} }),
		},
	),
)

/**
 * Generic hook for any persisted form draft, keyed by a string.
 * Use for forms without explicit step tracking — pass `stepIdx: 0` if irrelevant.
 */
export function useFormDraft<T>(key: string) {
	const get   = useWizardDraftsStore((s) => s.getDraft)
	const set   = useWizardDraftsStore((s) => s.setDraft)
	const clear = useWizardDraftsStore((s) => s.clearDraft)

	const read = useCallback((version?: string): { state: T; stepIdx: number } | undefined => {
		const d = get(key)
		if (!d) return undefined
		// Запись изменилась с тех пор, как черновик записали — он устарел.
		if (version !== undefined && d.version !== undefined && d.version !== version) {
			return undefined
		}
		return { state: d.state as T, stepIdx: d.stepIdx }
	}, [get, key])

	const write = useCallback((state: T, stepIdx = 0, version?: string) => {
		set(key, { state, stepIdx, version })
	}, [set, key])

	const reset = useCallback(() => {
		clear(key)
	}, [clear, key])

	return { read, write, reset }
}
